import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Card, CardContent, useMediaQuery, useTheme } from "@mui/material";
import styles from "./WeeklyCalendar.module.css";
import {
  getDayName,
  getWeekStart,
  getWeekDates,
  formatDateToString,
  isToday,
  isSameDay,
  IWeeklyCalendarProps,
  ICalendarEvent,
} from "./CalendarUtils";

/**
 * Generate time slots for the calendar
 */
const generateTimeSlots = (startHour = 0, endHour = 24): string[] => {
  const slots: string[] = [];
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${String(hour).padStart(2, "0")}:00`);
  }
  return slots;
};

/**
 * Reusable WeeklyCalendar component with time slots
 * Features:
 * - Weekly schedule view with time slots on the left
 * - 8:00-17:00 focused range
 * - Romanian localization
 * - Event display in time slots
 * - Optimized with useMemo and useCallback
 */
export default function WeeklyCalendar({
  events = [],
  onDateSelect,
  onEventClick,
  initialDate = new Date(),
  className,
  currentDate: externalCurrentDate,
  timeSlots: customTimeSlots,
  showTimeSlots = true,
  renderEventCard,
}: IWeeklyCalendarProps) {
  const [internalCurrentDate] = useState(initialDate);
  const currentDate = externalCurrentDate || internalCurrentDate;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get week start date (Monday) - memoized
  const weekStart = useMemo(() => getWeekStart(currentDate), [currentDate]);

  // Get all dates for the week - memoized
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // Day names array - memoized
  const dayNames = useMemo(
    () => [1, 2, 3, 4, 5, 6, 0].map((dayIndex) => getDayName(dayIndex)),
    []
  );

  // Generate all time slots (full day 0-24) - memoized
  const allTimeSlots = useMemo(
    () => customTimeSlots || generateTimeSlots(0, 24),
    [customTimeSlots]
  );

  // Reference to the focus time slot (8:00) for scrolling
  const focusTimeRef = useRef<HTMLDivElement>(null);

  // Scroll to focus time (8:00) on mount
  useEffect(() => {
    if (focusTimeRef.current) {
      focusTimeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const SLOT_MINUTES = 60; // 60 minutes per slot

  /**
   * Calculate how many time slots an event should span
   */
  const getEventSpanSlots = useCallback((event: ICalendarEvent): number => {
  if (!event.startDate || !event.endDate) return 1;

  const start = new Date(event.startDate);
  const end = new Date(event.endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = diffMs / (1000 * 60);

  const slots = Math.max(1, diffMinutes / SLOT_MINUTES);

  return slots;
}, []);

  /**
   * Get events for a specific date and time slot
   */
  const getEventsForTimeSlot = useCallback(
    (date: Date, timeSlot: string): ICalendarEvent[] => {
      return events.filter((event) => {
        if (event.startDate) {
          const eventStart = new Date(event.startDate);
          const [hours, minutes] = timeSlot.split(":");
          const slotDate = new Date(date);
          slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

          return (
            formatDateToString(slotDate) === formatDateToString(eventStart) &&
            slotDate.getHours() === eventStart.getHours()
          );
        }

        const dateStr = formatDateToString(date);
        if (!event.date.startsWith(dateStr)) return false;
        if (event.date.includes(timeSlot)) return true;
        return false;
      });
    },
    [events]
  );

  /**
   * Render time slot cell
   */
  const renderTimeSlotCell = useCallback(
    (date: Date, timeSlot: string) => {
      const slotEvents = getEventsForTimeSlot(date, timeSlot);
      const hasEvents = slotEvents.length > 0;
      const isTodayDate = isToday(date);

      return (
        <Box
          key={`${formatDateToString(date)}-${timeSlot}`}
          className={`${styles.timeSlotCell} ${hasEvents ? styles.hasEvents : ""} ${
            isTodayDate ? styles.today : ""
          }`}
          style={{ cursor: hasEvents ? 'pointer' : 'default' }}
        >
          {hasEvents &&
            slotEvents.map((event) => {
              const spanSlots = getEventSpanSlots(event);
              const minHeight = spanSlots * 60 - 8;
              return (
                <Box
                  key={event.id}
                  className={styles.eventBlock}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick?.(event);
                  }}
                  style={{
                    minHeight: `${minHeight}px`,
                    width: '100%',
                  }}
                >
                  {renderEventCard ? (
                    renderEventCard(event)
                  ) : (
                    <Typography variant="caption" className={styles.eventTitle}>
                      {event.title}
                    </Typography>
                  )}
                </Box>
              );
            })}
        </Box>
      );
    },
    [
      getEventsForTimeSlot,
      getEventSpanSlots,
      renderEventCard,
      onEventClick,
    ]
  );

  // State for selected day on mobile - default to today if it's in current week
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const today = new Date();
    const todayIndex = weekDates.findIndex(date => isSameDay(date, today));
    return todayIndex !== -1 ? todayIndex : 0;
  });

  // Mobile rendering: Single day calendar with horizontal day selector
  const renderMobileView = () => {
    const selectedDate = weekDates[selectedDayIndex];
    const isTodayDate = isToday(selectedDate);

    return (
      <Box className={styles.mobileCalendar}>
        {/* Day selector - horizontal scroll */}
        <Box className={styles.mobileDaySelector}>
          {weekDates.map((date, dayIndex) => {
            const isTodayDay = isToday(date);
            const isSelected = dayIndex === selectedDayIndex;
            return (
              <Box
                key={dayIndex}
                className={`${styles.mobileDayTab} ${
                  isSelected ? styles.selectedDay : ""
                } ${isTodayDay ? styles.todayTab : ""}`}
                onClick={() => setSelectedDayIndex(dayIndex)}
              >
                <Typography className={styles.mobileDayTabName}>
                  {dayNames[dayIndex].substring(0, 3)}
                </Typography>
                <Typography className={styles.mobileDayTabDate}>
                  {date.getDate()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Selected day header */}
        <Box
          className={`${styles.mobileSelectedDayHeader} ${
            isTodayDate ? styles.todayHeader : ""
          }`}
        >
          <Typography className={styles.mobileSelectedDayName}>
            {dayNames[selectedDayIndex]}
          </Typography>
          <Typography className={styles.mobileSelectedDayDate}>
            {selectedDate.getDate()}{" "}
            {selectedDate.toLocaleDateString("ro-RO", { month: "long", year: "numeric" })}
          </Typography>
        </Box>

        {/* Time slots for selected day */}
        <Box className={styles.mobileTimeSlotsList}>
          {allTimeSlots.map((timeSlot) => {
            const slotEvents = getEventsForTimeSlot(selectedDate, timeSlot);
            const hasEvents = slotEvents.length > 0;

            // Calculate event height based on duration (1.5 hours = 105px)
            let eventHeight = 70; // default min height
            if (hasEvents && slotEvents[0]) {
              const spanSlots = getEventSpanSlots(slotEvents[0]);
              eventHeight = Math.max(70, spanSlots * 70); // 70px per hour slot
            }

            return (
              <Box
                key={timeSlot}
                className={`${styles.mobileTimeSlot} ${
                  hasEvents ? styles.hasEvent : ""
                }`}
                style={{ minHeight: `${eventHeight}px` }}
              >
                <Box className={styles.mobileTimeLabel}>
                  <Typography className={styles.mobileTimeText}>
                    {timeSlot}
                  </Typography>
                </Box>
                <Box
                  className={`${styles.mobileEventContainer} ${
                    hasEvents ? styles.hasEventSlot : ""
                  }`}
                  onClick={hasEvents && slotEvents[0] ? () => onEventClick?.(slotEvents[0]) : undefined}
                  style={{ cursor: hasEvents ? 'pointer' : 'default' }}
                >
                  {hasEvents && slotEvents[0] ? (
                    renderEventCard ? (
                      renderEventCard(slotEvents[0])
                    ) : (
                      <Typography style={{ color: "white", fontWeight: 600 }}>
                        {slotEvents[0].title}
                      </Typography>
                    )
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // If mobile, render mobile view
  if (isMobile) {
    return (
      <Card className={`${styles.weeklyCalendarCard} ${className || ""}`}>
        <CardContent className={styles.calendarContent}>
          {renderMobileView()}
        </CardContent>
      </Card>
    );
  }

  // Desktop rendering: Original grid layout
  return (
    <Card className={`${styles.weeklyCalendarCard} ${className || ""}`}>
      <CardContent className={styles.calendarContent}>
        {/* Header with day names and dates */}
        <Box className={styles.calendarHeader}>
          {/* Empty corner cell for time column */}
          <Box className={styles.timeColumnHeader} />

          {/* Day headers */}
          {weekDates.map((date, index) => {
            const isTodayDate = isToday(date);
            return (
              <Box
                key={index}
                className={`${styles.dayHeaderCell} ${
                  isTodayDate ? styles.todayHeader : ""
                }`}
              >
                <Typography variant="caption" className={styles.dayName}>
                  {dayNames[index]}
                </Typography>
                <Typography
                  variant="h6"
                  className={`${styles.dayDate} ${
                    isTodayDate ? styles.todayNumber : ""
                  }`}
                >
                  {date.getDate()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Time slots grid - All 24 hours */}
        <Box className={styles.scheduleGrid}>
          {allTimeSlots.map((timeSlot) => {
            const hour = parseInt(timeSlot.split(":")[0]);
            const isFocusHour = hour === 8; // Focus on 8:00

            return (
              <Box
                key={timeSlot}
                className={styles.timeRow}
                ref={isFocusHour ? focusTimeRef : null}
              >
                {/* Time label */}
                <Box className={styles.timeLabel}>
                  <Typography variant="body2" className={styles.timeText}>
                    {timeSlot}
                  </Typography>
                </Box>

                {/* Time slot cells for each day */}
                {weekDates.map((date) => renderTimeSlotCell(date, timeSlot))}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}